import React from 'react';

import { useLogoutMutation } from '@@/services/auth';
import { useGetMeQuery } from '@@/services/user';

import { User, Shield, LogOut, Mail, Building2, Loader2 } from 'lucide-react';

const MePage: React.FC = () => {
  const { data: me, isLoading, error } = useGetMeQuery();
  const [logout] = useLogoutMutation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 size={48} className="text-primary-600 animate-spin" />
          <p className="text-neutral-600 font-medium">
            Chargement de votre profil...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={32} className="text-danger-600" />
            </div>
            <h2 className="text-xl font-bold text-neutral-900 mb-2">
              Erreur de chargement
            </h2>
            <p className="text-neutral-600">
              Impossible de charger vos informations. Veuillez réessayer.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User size={32} className="text-neutral-400" />
            </div>
            <h2 className="text-xl font-bold text-neutral-900 mb-2">
              Aucune donnée
            </h2>
            <p className="text-neutral-600">
              Aucune information de profil disponible.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-blue-50 to-primary-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <User size={20} className="text-white sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 truncate">
                Mon profil
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 truncate">
                Gérez vos informations personnelles et paramètres
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
        {/* Section Informations personnelles */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-primary-100">
            <div className="flex items-center space-x-3">
              <User size={18} className="text-primary-600 sm:w-5 sm:h-5" />
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900">
                Informations personnelles
              </h2>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-4">
            {/* Nom */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-5">
              <label className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wide">
                Nom
              </label>
              <p className="mt-1 text-base sm:text-lg font-semibold text-gray-900 break-words">
                {me.name}
              </p>
            </div>

            {/* Email */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-5">
              <label className="text-xs sm:text-sm font-medium text-neutral-500 uppercase tracking-wide flex items-center space-x-2">
                <Mail size={14} className="sm:w-4 sm:h-4" />
                <span>Email</span>
              </label>
              <p className="mt-1 text-base sm:text-lg font-semibold text-neutral-900 break-all">
                {me.email}
              </p>
            </div>

            {/* Partenaire */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-5">
              <label className="text-xs sm:text-sm font-medium text-neutral-500 uppercase tracking-wide flex items-center space-x-2">
                <Building2 size={14} className="sm:w-4 sm:h-4" />
                <span>Partenaire</span>
              </label>
              <div className="mt-2 flex items-center space-x-3">
                {me.partner.icon && (
                  <img
                    src={me.partner.icon}
                    alt={me.partner.name}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-contain bg-white p-1"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-base sm:text-lg font-semibold text-neutral-900 break-words">
                    {me.partner.name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section Sécurité */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-danger-50 to-pink-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-danger-100">
            <div className="flex items-center space-x-3">
              <Shield size={18} className="text-danger-600 sm:w-5 sm:h-5" />
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900">
                Sécurité et confidentialité
              </h2>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-6">
              <h3 className="font-semibold text-neutral-900 mb-3 text-sm sm:text-base">
                Session et connexion
              </h3>
              <p className="text-neutral-600 text-xs sm:text-sm mb-4">
                Vous pouvez vous déconnecter de votre session actuelle. Vous
                devrez vous reconnecter pour accéder à votre compte.
              </p>
              <button
                onClick={() => logout()}
                className="inline-flex items-center px-4 py-2 bg-danger-600 hover:bg-danger-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm sm:text-base w-full sm:w-auto justify-center"
              >
                <LogOut size={16} className="mr-2 sm:w-[18px] sm:h-[18px]" />
                Se déconnecter
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MePage;
